import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { FaCrosshairs } from 'react-icons/fa';
import { FaChevronDown } from 'react-icons/fa';
import './styles/mapbox-popup-fix.css';
import './map-cursor-mix.css';
// Hide the Mapbox popup close button entirely
const hidePopupCloseButton = () => {
  const style = document.createElement('style');
  style.innerHTML = `.mapboxgl-popup-close-button { display: none !important; }`;
  document.head.appendChild(style);
};
hidePopupCloseButton();

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const GEOJSON_URL = '/geocoded.json'; // Assumes geocoded.json is in public/
export default function TestMap({ isMobile }) {
  const mapContainer = useRef(null); // This is for the mapbox instance container
  const testMapViewRootRef = useRef(null); // This is for the root div of TestMap component
  const map = useRef(null);
  const popupRef = useRef(null); // Track the currently open popup
  const [points, setPoints] = useState([]);
  // Removed filter and showDropdown state
  const [loading, setLoading] = useState(true);

  // Load geocoded points
  useEffect(() => {
    fetch(GEOJSON_URL)
      .then(res => res.json())
      .then(data => {
        setPoints(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Plot markers and fit bounds
  useEffect(() => {
    if (!map.current || loading || !points.length) return; // Added !points.length check
    // Remove existing markers
    if (map.current._markers) {
      map.current._markers.forEach(m => m.remove());
    }
    // All points are now 'filtered' by default
    const filtered = points;
    console.log('All loaded points (no filtering):', points);
    // Add markers
    map.current._markers = (filtered || []).map(p => {
      if (!p.lat || !p.lon) return null;
      // Try to get neighborhood/subdivision from p.raw
      const raw = p.raw || {};
      const now = new Date().getFullYear();
let neighborhood = (raw['Neighborhood'] || '').trim();
if (!neighborhood || neighborhood.toLowerCase() === 'n/a' || neighborhood.toLowerCase() === 'unknown') {
  neighborhood = raw['City'] || '';
}
let year = '';
if (raw['Year'] && /^\d{4}$/.test(raw['Year']) && +raw['Year'] >= 1900 && +raw['Year'] <= now) {
  year = raw['Year'];
}

      const photoUrl = raw['Photo URL'] || null;

      const popupHtml = `
        <div style='width: 170px; border-radius: 10px; overflow: hidden; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.15); font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;'>
          <div style='
            position: relative;
            width: 100%;
            padding-bottom: 60%;
            background: #f0f0f0;
            border-radius: 10px 10px 0 0;
            overflow: hidden;'>
            <img 
              src='${photoUrl}' 
              alt='${neighborhood || 'Property image'}' 
              style='
                position: absolute;
                width: 100%;
                height: 100%;
                object-fit: cover;'/>
          </div>
          <div style='
            padding: 10px 12px 0px 12px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 600;
            font-size: 13px;
            color: #222;'>${neighborhood}</div>
          ${year ? `
            <div style='
              padding: 0 12px 10px 12px;
              font-size: 12px;
              color: #666;'>Sold: ${year}</div>
          ` : ''}
        </div>
      `;
      const popup = new mapboxgl.Popup({ offset: 16 }).setHTML(popupHtml);
      const marker = new mapboxgl.Marker({ color: '#003DA5', scale: 0.8 })
        .setLngLat([parseFloat(p.lon), parseFloat(p.lat)])
        .setPopup(popup);
      
      // Track when this popup opens
      marker.getElement().addEventListener('click', () => {
        // Close any existing popup
        if (popupRef.current) {
          popupRef.current.remove();
        }
        // Store this popup reference
        popupRef.current = popup;
      });
      
      marker.addTo(map.current);
      return marker;
    }).filter(Boolean);
    // Fit map to bounds
    if (filtered.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      filtered.forEach(p => {
        if (p.lat && p.lon) bounds.extend([parseFloat(p.lon), parseFloat(p.lat)]);
      });
      const bottomPadding = isMobile ? 50 : 50; // 50px for mobile, 50px for desktop
      map.current.fitBounds(bounds, { 
        padding: { top: 50, bottom: bottomPadding, left: 60, right: 60 },
        maxZoom: 13,
        duration: 1000 // Added for smooth 1-second animation
      });
// Removed Bend-specific flyTo logic
    } else {
      console.warn('No points to plot on the map!');
    }
  }, [points, loading, isMobile]);

  // Init map
  useEffect(() => {
    if (!mapContainer.current) return;
    console.log('About to initialize Mapbox map', mapContainer.current);
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-121.3153, 44.0582],
      zoom: 8,
      attributionControl: true, // Enable default attribution
    });
    // Position navigation controls at bottom-right - only on desktop
    if (!isMobile) {
      map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    }
    
    console.log('Mapbox map initialized (default attribution)', map.current);
    return () => map.current?.remove();
  }, [isMobile]);

  // Effect to handle map resizing using ResizeObserver
  useEffect(() => {
    const currentMapInstance = map.current;
    const mapRootElement = testMapViewRootRef.current; // The element being observed

    if (!currentMapInstance || !mapRootElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      currentMapInstance.resize();
    });

    resizeObserver.observe(mapRootElement);

    // Initial resize call
    requestAnimationFrame(() => {
      currentMapInstance.resize();
    });

    return () => {
      resizeObserver.unobserve(mapRootElement);
      resizeObserver.disconnect();
    };
  }, []); // Runs once after initial mount and map/container refs are set

  const handleRecenterMap = () => {
    const currentMap = map.current;
    if (!currentMap || !points || points.length === 0) {
      console.log('Recenter: No map or no points to recenter.');
      return;
    }

    // Close any open popup before recentering
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    const bounds = new mapboxgl.LngLatBounds();
    let validPointsExist = false;
    points.forEach(p => {
      if (p.lat && p.lon) {
        bounds.extend([parseFloat(p.lon), parseFloat(p.lat)]);
        validPointsExist = true;
      }
    });

    if (validPointsExist) {
      const bottomPadding = isMobile ? 50 : 50;
      currentMap.fitBounds(bounds, { 
        padding: { top: 50, bottom: bottomPadding, left: 60, right: 60 },
        maxZoom: 13,
        duration: 1000
      });
    } else {
      console.log('Recenter: No valid points found in historical data.');
    }
  };

  return (
    <div ref={testMapViewRootRef} className="map-side" style={{
      position: 'relative', // For positioning the absolute map container
      width: '100%',
      height: '100%',
      margin: 0,
      padding: 0,
      overflow: 'hidden', // Important to contain the map
      boxSizing: 'border-box'
    }}>
      {/* Region filter dropdown removed */}
      <div
        ref={mapContainer}
        className="map-container"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%', // Explicit width/height can be redundant but safe
          height: '100%'
        }}
      />
      <button
        onClick={e => { handleRecenterMap(); if (e.currentTarget) e.currentTarget.blur(); }}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 2, // Ensure it's above map tiles and other controls
          background: 'white',
          border: '1px solid #dbdbdb',
          borderRadius: '4px',
          padding: '8px',
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: '1'
        }}
        title="Recenter Map"
      >
        <FaCrosshairs size={18} color="#333" />
      </button>
      {/* Loading indicator removed */}
    </div>
  );
}
